// Right-pane file list. Supports both grid and table views, server-side
// pagination, double-click rename, "Taşı" + delete actions.

import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Dropdown,
  Empty,
  Input,
  Pagination,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowRightLeft,
  Download,
  MoreVertical,
  Pencil,
  TriangleAlert,
  Trash2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { filesApi, type FileItem } from '@api/files';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { formatBytes, isImageMime } from '../utils';
import { FileThumbnail } from './FileThumbnail';
import { MoveFileModal } from './MoveFileModal';

const { Text } = Typography;

/** BIZ-IMG-03 — an image with no alt text is an SEO gap; flag it in the list. */
function needsAltText(file: FileItem): boolean {
  return isImageMime(file.mimeType, file.fileName) && !file.altText?.trim();
}

function SeoAltWarning({ label }: { label: string }) {
  return (
    <Tooltip title={label}>
      <TriangleAlert size={14} color="var(--ds-warning, #d97706)" aria-label={label} />
    </Tooltip>
  );
}

interface FileListProps {
  items: FileItem[];
  loading: boolean;
  view: 'grid' | 'list';
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onSelect: (file: FileItem) => void;
}

interface InlineRenameState {
  id: string;
  value: string;
}

export function FileList({
  items,
  loading,
  view,
  page,
  pageSize,
  total,
  onPageChange,
  onSelect,
}: FileListProps) {
  const { t } = useTranslation(['media', 'common']);
  const qc = useQueryClient();
  const { message } = App.useApp();
  const confirm = useConfirm();
  const [rename, setRename] = useState<InlineRenameState | null>(null);
  const [moveTarget, setMoveTarget] = useState<FileItem | null>(null);

  // `invalidate` awaits both the files list and the folder tree so the toast +
  // modal close happen after the list has actually refetched. Without `await`
  // the move would resolve before refetch settled, leaving the moved row on
  // screen until the next user interaction.
  const invalidate = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['files'], refetchType: 'all' }),
      qc.invalidateQueries({ queryKey: ['folders'], refetchType: 'all' }),
    ]);
  };

  const renameMut = useMutation({
    mutationFn: (vars: { id: string; name: string; rowVersion: string | null }) =>
      filesApi.rename(vars.id, vars.name, vars.rowVersion),
    onSuccess: async () => {
      void message.success(t('fileList.toast.renamed'));
      setRename(null);
      await invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const moveMut = useMutation({
    mutationFn: (vars: { id: string; targetFolderId: string | null }) =>
      filesApi.move(vars.id, vars.targetFolderId),
    onSuccess: async (_d, vars) => {
      setMoveTarget(null);
      // Optimistically strip the moved row from every cached `['files','list',…]`
      // query so the current folder updates instantly even if the background
      // refetch is delayed by the network or the BE's commit timing.
      qc.setQueriesData<{ items: FileItem[]; total: number; pagination: unknown } | undefined>(
        { queryKey: ['files', 'list'] },
        (prev) => {
          if (!prev) return prev;
          const filtered = prev.items.filter((it) => it.id !== vars.id);
          if (filtered.length === prev.items.length) return prev;
          return {
            ...prev,
            items: filtered,
            total: Math.max(0, (prev.total ?? filtered.length) - 1),
          };
        },
      );
      await invalidate();
      void message.success(t('fileList.toast.moved'));
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => filesApi.remove(id),
    onSuccess: async () => {
      void message.success(t('fileList.toast.deleted'));
      await invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const confirmDelete = (file: FileItem) => {
    void confirm({
      title: t('fileList.deleteConfirm.title'),
      description: t('fileList.deleteConfirm.description', { name: file.fileName }),
      okText: t('fileList.deleteConfirm.okText'),
      cancelText: t('fileList.deleteConfirm.cancelText'),
      danger: true,
    }).then((ok) => {
      if (ok) deleteMut.mutate(file.id);
    });
  };

  const submitRename = (file: FileItem) => {
    if (!rename) return;
    const value = rename.value.trim();
    if (!value || value === file.fileName) {
      setRename(null);
      return;
    }
    renameMut.mutate({ id: file.id, name: value, rowVersion: file.rowVersion });
  };

  const actionMenu = (file: FileItem) => ({
    items: [
      { key: 'rename', icon: <Pencil size={12} />, label: t('fileList.actions.rename') },
      { key: 'move', icon: <ArrowRightLeft size={12} />, label: t('fileList.actions.move') },
      { key: 'download', icon: <Download size={12} />, label: t('fileList.actions.download') },
      { type: 'divider' as const },
      { key: 'delete', icon: <Trash2 size={12} />, label: t('fileList.actions.delete'), danger: true },
    ],
    onClick: ({ key, domEvent }: { key: string; domEvent: React.MouseEvent | React.KeyboardEvent }) => {
      domEvent.stopPropagation();
      if (key === 'rename') {
        setRename({ id: file.id, value: file.fileName });
      } else if (key === 'move') {
        setMoveTarget(file);
      } else if (key === 'download') {
        void filesApi.download(file.id).then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        });
      } else if (key === 'delete') {
        confirmDelete(file);
      }
    },
  });

  const columns: ColumnsType<FileItem> = [
    {
      title: '',
      key: 'thumb',
      width: 56,
      render: (_: unknown, f) => (
        <FileThumbnail id={f.id} fileName={f.fileName} mimeType={f.mimeType} size={40} />
      ),
    },
    {
      title: t('fileList.columns.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      render: (_: unknown, f) =>
        rename?.id === f.id ? (
          // eslint-disable-next-line jsx-a11y/no-autofocus -- inline rename: focus the field the user just opened
          <Input autoFocus
            size="small"
            value={rename.value}
            onChange={(e) => setRename({ id: f.id, value: e.target.value })}
            onPressEnter={() => submitRename(f)}
            onBlur={() => submitRename(f)}
            data-testid="file-rename-input"
          />
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onDoubleClick={(e) => {
                e.stopPropagation();
                setRename({ id: f.id, value: f.fileName });
              }}
              onClick={() => onSelect(f)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--ds-text-primary, inherit)',
                textAlign: 'left',
              }}
            >
              {f.fileName}
            </button>
            {needsAltText(f) ? <SeoAltWarning label={t('fileList.seoAltWarning')} /> : null}
          </span>
        ),
    },
    {
      title: t('fileList.columns.mimeType'),
      dataIndex: 'mimeType',
      key: 'mimeType',
      width: 160,
      responsive: ['md'],
      render: (m: string) => <Tag>{m || 'application/octet-stream'}</Tag>,
    },
    {
      title: t('fileList.columns.sizeBytes'),
      dataIndex: 'sizeBytes',
      key: 'sizeBytes',
      width: 100,
      align: 'right' as const,
      responsive: ['sm'],
      render: (b: number) => formatBytes(b),
    },
    {
      title: t('fileList.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      responsive: ['lg'],
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 44,
      render: (_: unknown, f) => (
        <Dropdown menu={actionMenu(f)} trigger={['click']}>
          <Button
            type="text"
            size="small"
            icon={<MoreVertical size={14} />}
            onClick={(e) => e.stopPropagation()}
            aria-label={t('fileList.actions.menuLabel')}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div data-testid="file-list">
      {view === 'list' ? (
        <Table<FileItem>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={false}
          size="small"
          locale={{ emptyText: <Empty description={t('fileList.empty')} /> }}
        />
      ) : loading ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} size="small">
              <Skeleton.Image active style={{ width: '100%', height: 96 }} />
              <Skeleton active title={false} paragraph={{ rows: 1 }} style={{ marginTop: 8 }} />
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty description={t('fileList.empty')} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          {items.map((f) => (
            <Card
              key={f.id}
              size="small"
              hoverable
              onClick={() => onSelect(f)}
              styles={{ body: { padding: 10 } }}
              data-testid={`file-card-${f.id}`}
            >
              <div style={{ display: 'grid', placeItems: 'center', marginBottom: 8 }}>
                <FileThumbnail
                  id={f.id}
                  fileName={f.fileName}
                  mimeType={f.mimeType}
                  size={120}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  justifyContent: 'space-between',
                }}
              >
                {rename?.id === f.id ? (
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- inline rename: focus the field the user just opened
                  <Input autoFocus
                    size="small"
                    value={rename.value}
                    onChange={(e) => setRename({ id: f.id, value: e.target.value })}
                    onPressEnter={() => submitRename(f)}
                    onBlur={() => submitRename(f)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="file-rename-input"
                  />
                ) : (
                  <Text
                    style={{ fontSize: 12, flex: 1, wordBreak: 'break-all' }}
                    ellipsis={{ tooltip: f.fileName }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setRename({ id: f.id, value: f.fileName });
                    }}
                  >
                    {f.fileName}
                  </Text>
                )}
                <Dropdown menu={actionMenu(f)} trigger={['click']}>
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreVertical size={12} />}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Dosya eylemleri"
                  />
                </Dropdown>
              </div>
              <Space size={4} style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatBytes(f.sizeBytes)}
                </Text>
                {needsAltText(f) ? <SeoAltWarning label={t('fileList.seoAltWarning')} /> : null}
              </Space>
            </Card>
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={onPageChange}
            showSizeChanger
            pageSizeOptions={[12, 24, 48, 96]}
            size="small"
          />
        </div>
      ) : null}

      <MoveFileModal
        open={moveTarget !== null}
        currentFolderId={moveTarget?.folderId ?? null}
        onCancel={() => setMoveTarget(null)}
        onConfirm={(target) => {
          if (moveTarget) moveMut.mutate({ id: moveTarget.id, targetFolderId: target });
        }}
      />
    </div>
  );
}
